import { View } from "react-native"
import React, { forwardRef } from "react"
import type { WithBordersProps } from "../../types/with-borders.type"

/**
 * Higher Order Component that adds borders to a wrapped component if the withBorders prop is true.
 *
 * @param WrappedComponent - The component to be wrapped.
 * @returns A new component with an optional border.
 */
export const withBorders = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
) => {
  const ComponentWithBorders = forwardRef<View, P & WithBordersProps>(
    (props, ref) => {
      const { withBorders = false, borderColor = "red", ...otherProps } = props

      if (withBorders) {
        return (
          <View
            style={{
              borderWidth: 1,
              borderColor: borderColor,
              alignSelf: "flex-start",
            }}
          >
            <WrappedComponent
              ref={ref}
              {...(otherProps as P)}
              withBorders={true}
            />
          </View>
        )
      }

      return <WrappedComponent ref={ref} {...(otherProps as P)} />
    },
  )

  // Set a display name for the wrapped component
  ComponentWithBorders.displayName = `withBorders(${
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  })`

  return ComponentWithBorders
}
